import { useEffect, useState } from "react";
import { Bell, Menu } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

export default function Topbar({ onMenuClick }) {
    const [unread, setUnread] = useState(0);
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();

    useEffect(() => {
        let mounted = true;
        const fetch = async () => {
            try {
                const { data } = await api.get("/notifications");
                if (mounted) setUnread(data.unread || 0);
            } catch {}
        };
        fetch();
        const t = setInterval(fetch, 30000);
        return () => {
            mounted = false;
            clearInterval(t);
        };
    }, []);

    // Helper to get active page title
    const getPageTitle = () => {
        const path = location.pathname;
        if (path === "/dashboard") return "Dashboard";
        if (path === "/employees") return "Employees";
        if (path.startsWith("/employees/")) return "Employee Profile";
        if (path === "/attendance") return "Attendance";
        if (path === "/leaves") return "Leaves";
        if (path === "/meetings") return "Meetings";
        if (path === "/notifications") return "Notices";
        if (path === "/projects") return "Projects";
        if (path === "/tasks") return "Tasks";
        if (path === "/payroll") return "Payroll";
        if (path === "/forms") return "Forms";
        if (path === "/team") return "Team";
        if (path === "/profile") return "Profile";
        
        // fallback
        const clean = path.replace("/", "").replace("-", " ");
        return clean.charAt(0).toUpperCase() + clean.slice(1);
    };

    const pageTitle = getPageTitle();

    return (
        <header className="sticky top-0 z-20 h-16 px-4 lg:px-8 flex items-center justify-between
            bg-white/60 dark:bg-[hsl(var(--surface))]/40 backdrop-blur-xl
            border-b border-black/5 dark:border-white/10">
            <div className="flex items-center gap-3">
                <button
                    onClick={onMenuClick}
                    className="lg:hidden p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 text-foreground"
                    data-testid="topbar-menu"
                >
                    <Menu className="w-5 h-5" />
                </button>
                <h1 className="text-sm font-semibold font-heading text-slate-800 dark:text-slate-200 tracking-wide uppercase">
                    {pageTitle}
                </h1>
            </div>

            <div className="flex items-center gap-4">
                {/* Synced status badge */}
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-600 border border-emerald-100 dark:bg-emerald-950/20 dark:text-emerald-400 dark:border-emerald-900/30">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    <span>Synced</span>
                </div>

                {/* Notifications bell */}
                <button
                    onClick={() => navigate("/notifications")}
                    className="relative p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 text-muted-foreground hover:text-foreground transition"
                    data-testid="topbar-notifications"
                >
                    <Bell className="w-4.5 h-4.5" />
                    {unread > 0 && (
                        <span className="absolute top-1 right-1 w-3.5 h-3.5 rounded-full bg-primary text-white text-[9px] font-semibold grid place-items-center">
                            {unread > 9 ? "9+" : unread}
                        </span>
                    )}
                </button>

                {/* User avatar + name */}
                <div 
                    onClick={() => navigate("/profile")}
                    className="flex items-center gap-2 border-l border-black/5 dark:border-white/10 pl-3 cursor-pointer hover:opacity-80 transition"
                >
                    {user?.avatar ? (
                        <img src={user.avatar} alt={user.name} className="w-7 h-7 rounded-full object-cover shadow-sm" />
                    ) : (
                        <div className="w-7 h-7 rounded-full bg-slate-900 text-white grid place-items-center text-[10px] font-semibold shadow-sm uppercase">
                            {user?.name?.[0]?.toUpperCase() || "G"}
                        </div>
                    )}
                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 hidden sm:inline-block">
                        {user?.name?.split(" ")[0] || "Gauransh"}
                    </span>
                </div>
            </div>
        </header>
    );
}
